import { useMemo, useCallback, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { styled } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { useDreamSearchFilterContext } from "./DreamSearchFilterContext";
import { useDreamSearchContext } from "../../DreamSearchContext";
import type { Filters } from "@portal/app/_common/types/dreamSearch";
import { emptyFilters } from "../../utils/useQueryParamFiltersForDreamSearch";
import {
  programLevelFriendlyMap,
  programTagFriendlyMap,
  customerPaymentOptionFriendlyNameMap,
} from "@portal/app/_common/data/enumValueMaps";
import { VocationIndustryToName } from "@portal/app/_common/types/vocationIndustry";
import { vocationSubindustryFriendlyMap } from "@portal/app/_common/types/vocationSubindustry";
import { ProgramEnvironmentToNameMap } from "@portal/app/_common/lib/programHelpers";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";

// Filter grouping configuration
const FILTER_GROUPS: Record<string, { label: string; filters: (keyof Filters)[] }> = {
  environment: {
    label: "Environment",
    filters: ["program_environment"],
  },
  duration: {
    label: "Duration",
    filters: ["program_duration_days_lte"],
  },
  level: {
    label: "Program Level",
    filters: ["program_level_in"],
  },
  payment: {
    label: "Payment Options",
    filters: ["program_payment_options_type_in"],
  },
  tags: {
    label: "Features",
    filters: ["program_tags_in"],
  },
  vocation: {
    label: "Field of Study",
    filters: ["program_vocation_in", "program_vocation_industry"],
  },
  subindustry: {
    label: "Subindustry",
    filters: ["program_vocation_subindustries_in"],
  },
};

interface AppliedFilter {
  id: string;
  group: string;
  label: string;
  value: string | number | boolean;
  filterKey: keyof Filters;
}

export const AppliedFilterChips = () => {
  const { filters, setFilters } = useDreamSearchFilterContext();
  const { filterSettings } = useDreamSearchContext();
  const [isVocationChipExpanded, setIsVocationChipExpanded] = useState(false);

  // Create mappings for dynamic data
  const vocationMap = useMemo(() => {
    const map: Record<string, string> = {};
    filterSettings?.vocation?.forEach((vocation) => {
      map[vocation.id] = vocation.adjective || vocation.abbreviation || vocation.id;
    });
    return map;
  }, [filterSettings?.vocation]);

  // Function to get display label for any filter value
  const getDisplayLabel = useCallback(
    (filterKey: keyof Filters, value: string | number | boolean): string => {
      const valueStr = String(value);

      // Handle specific vocation-related filters first (before the general vocation check)
      if (filterKey === "program_vocation_subindustries_in") {
        return (
          vocationSubindustryFriendlyMap[valueStr as keyof typeof vocationSubindustryFriendlyMap] ||
          valueStr
        );
      }

      if (filterKey === "program_vocation_industry") {
        return VocationIndustryToName[valueStr as keyof typeof VocationIndustryToName] || valueStr;
      }

      if (filterKey === "program_vocation_in") {
        return vocationMap[valueStr] || valueStr;
      }

      // Handle program levels
      if (filterKey === "program_level_in") {
        return (
          programLevelFriendlyMap[valueStr as keyof typeof programLevelFriendlyMap] || valueStr
        );
      }

      // Handle program tags
      if (filterKey === "program_tags_in") {
        return programTagFriendlyMap[valueStr as keyof typeof programTagFriendlyMap] || valueStr;
      }

      // Handle environment
      if (filterKey === "program_environment") {
        return (
          ProgramEnvironmentToNameMap[valueStr as keyof typeof ProgramEnvironmentToNameMap] ||
          valueStr
        );
      }

      // Handle payment options
      if (filterKey === "program_payment_options_type_in") {
        return (
          customerPaymentOptionFriendlyNameMap[
            valueStr as keyof typeof customerPaymentOptionFriendlyNameMap
          ] || valueStr
        );
      }

      // Handle duration - convert to user-friendly format matching the filter options
      if (filterKey === "program_duration_days_lte" && typeof value === "number") {
        const durationMap: Record<number, string> = {
          90: "< 3 months",
          180: "< 6 months",
          365: "< 1 year",
          730: "< 2 years",
        };
        return durationMap[value] || `≤ ${value} days`;
      }

      // Fallback to raw value
      return valueStr;
    },
    [vocationMap]
  );

  const { vocationFilters, otherFilters } = useMemo(() => {
    const vocations: AppliedFilter[] = [];
    const others: AppliedFilter[] = [];

    Object.entries(filters).forEach(([filterKey, filterValue]) => {
      const key = filterKey as keyof Filters;

      if (
        !filterValue ||
        (Array.isArray(filterValue) && filterValue.length === 0) ||
        (typeof filterValue === "string" && filterValue.trim() === "") ||
        (typeof filterValue === "number" && filterValue === 0) ||
        (typeof filterValue === "boolean" && !filterValue)
      ) {
        return;
      }

      const groupEntry = Object.entries(FILTER_GROUPS).find(([, group]) =>
        (group.filters as readonly string[]).includes(key)
      );

      if (!groupEntry) return;

      const [, group] = groupEntry;

      const createAppliedFilter = (
        value: string | number | boolean,
        index?: number
      ): AppliedFilter => {
        const displayLabel = getDisplayLabel(key, value);
        return {
          id: index !== undefined ? `${String(key)}-${index}` : `${String(key)}-single`,
          group: group.label,
          label: displayLabel,
          value: value,
          filterKey: key,
        };
      };

      if (key === "program_vocation_in") {
        if (Array.isArray(filterValue)) {
          filterValue.forEach((value, index) => {
            vocations.push(createAppliedFilter(value, index));
          });
        }
      } else {
        if (Array.isArray(filterValue)) {
          filterValue.forEach((value, index) => {
            others.push(createAppliedFilter(value, index));
          });
        } else {
          others.push(createAppliedFilter(filterValue));
        }
      }
    });

    return { vocationFilters: vocations, otherFilters: others };
  }, [filters, getDisplayLabel]);

  const removeFilter = (appliedFilter: AppliedFilter) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters };
      const { filterKey, value } = appliedFilter;

      const currentValue = newFilters[filterKey];
      const emptyValue = emptyFilters[filterKey];

      if (Array.isArray(currentValue)) {
        // Remove specific value from array
        const filteredArray = currentValue.filter((item: unknown) => item !== value);

        // If array becomes empty, reset to the default empty state from emptyFilters
        (newFilters as Record<string, unknown>)[filterKey] =
          filteredArray.length === 0 ? emptyValue : filteredArray;
      } else {
        // Reset single value filter to its default from emptyFilters
        (newFilters as Record<string, unknown>)[filterKey] = emptyValue;
      }

      return newFilters;
    });
  };

  if (otherFilters.length === 0 && vocationFilters.length === 0) {
    return null;
  }

  return (
    <ChipsContainer>
      {otherFilters.map((filter) => (
        <FilterChip
          key={filter.id}
          label={filter.label}
          onDelete={() => removeFilter(filter)}
          deleteIcon={
            <DeleteIconWrapper>
              <FontAwesomeIcon icon={faXmark} size="xs" />
            </DeleteIconWrapper>
          }
          data-testid={`applied-filter-chip-${filter.filterKey}-${filter.value}`}
        />
      ))}
      {vocationFilters.length > 1 && !isVocationChipExpanded ? (
        <FilterChip
          label={`${vocationFilters[0].label} +${vocationFilters.length - 1} more`}
          onClick={() => setIsVocationChipExpanded(true)}
          onDelete={() => setIsVocationChipExpanded(true)}
          deleteIcon={
            <DeleteIconWrapper>
              <FontAwesomeIcon icon={faChevronDown} size="xs" />
            </DeleteIconWrapper>
          }
          data-testid="applied-filter-chip-vocation-combined"
        />
      ) : (
        vocationFilters.map((filter) => (
          <FilterChip
            key={filter.id}
            label={filter.label}
            onDelete={() => removeFilter(filter)}
            deleteIcon={
              <DeleteIconWrapper>
                <FontAwesomeIcon icon={faXmark} size="xs" />
              </DeleteIconWrapper>
            }
            data-testid={`applied-filter-chip-${filter.filterKey}-${filter.value}`}
          />
        ))
      )}
    </ChipsContainer>
  );
};

const ChipsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 1rem;

  ${({ theme }) => theme.breakpoints.up("md")} {
    margin-bottom: 1.5rem;
  }
`;

const FilterChip = styled(Chip)`
  background-color: ${({ theme }) => theme.palette.primary.dark};
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  height: auto;

  .MuiChip-label {
    color: white;
    font-weight: 500;
    padding-left: 0;
    padding-right: 8px;
  }

  .MuiChip-deleteIcon {
    color: white;
    margin-right: 0;
    margin-left: 8px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.dark};
    opacity: 0.9;

    .MuiChip-deleteIcon {
      background-color: rgba(255, 255, 255, 0.2);
    }
  }
`;

const DeleteIconWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;

  svg {
    width: 12px;
    height: 12px;
  }
`;
